/*--------------------------------------------------------------

* Copyright (c) Microsoft Corporation. All rights reserved.

* See License.txt in the project root for license information.

*--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */

/* eslint-disable security/detect-object-injection */

import * as MsDyn365 from '@msdyn365-commerce/core';

import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

import { getTelemetryObject, IAlertProps, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import classnames from 'classnames';

import * as React from 'react';

import { reaction } from 'mobx';

import { CookieComplianceAcceptButton, CookieComplianceLinks } from './components';

import { IDobbiesCookieComplianceConfig, IDobbiesCookieComplianceProps } from './dobbies-cookie-compliance.props.autogenerated';

import { IDobbiesCookieComplianceData } from './dobbies-cookie-compliance.data';

// Declare global types for analytics libraries

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[];

        clarity: (command: string, options: Record<string, string>) => void;

        /**

         * Current analytics consent state used by THG Chatbot.

         * undefined = consent has not been decided yet.

         */

        DobbiesTHGConsent?: {
            analytics: boolean | undefined;
        };

        THGChatbot: {
            trackSiteEvent: (event: string, data: Record<string, unknown>) => void;
        };
    }

    function gtag(command: string, action: string, options?: Record<string, string | number>): void;
}

/**

* Alert state interface.

*/

export interface ICheckboxState {
    name: CheckboxType;

    value: boolean;
}

export interface IAlertState {
    visible: boolean;

    checkboxesChecked: ICheckboxState[];

    showModal?: boolean;
}

export enum CheckboxType {
    Functional = 'Functional',

    Analytics = 'Analytics',

    Marketing = 'Marketing'
}

/**

* Cookie compliance view props interface.

*/

export interface ICookieComplianceViewProps extends IDobbiesCookieComplianceProps<{}> {
    CookieComplianceBanner: IModuleProps;

    AlertProps: IAlertProps;

    Content: INodeProps;

    text?: React.ReactNode;

    checkboxes?: React.ReactNode;

    links?: React.ReactNode;

    acceptButton?: React.ReactNode;

    className: string;

    showCookieModal?: boolean;

    onDismiss(): void;

    onRejectAll?(): void;

    onSaveSettings?(): void;

    onManageCookiesClicked?(): void;

    onManageCookiesModalClose?(): void;

    showModal?: boolean;

    isModuleVisible?: boolean;
}

interface ICookieConsentEventData {
    acceptedAnalyticalCookies: boolean;

    acceptedFunctionalCookies: boolean;

    acceptedMarketingCookies: boolean;
}

/**

* Returns the current analytics consent state for THG Chatbot.

*

* THG expects one of:

* - granted

* - denied

* - unknown

* - not_required

*

* The CMP remains the source of truth for the consent decision.

*/

export const getTHGConsentState = (): 'granted' | 'denied' | 'unknown' | 'not_required' => {
    if (!MsDyn365.msdyn365Commerce.isBrowser || typeof window === 'undefined') {
        return 'unknown';
    }

    const analyticsConsent = window.DobbiesTHGConsent?.analytics;

    if (analyticsConsent === true) {
        return 'granted';
    }

    if (analyticsConsent === false) {
        return 'denied';
    }

    return 'unknown';
};

/**

*

* CookieCompliance component.

* @extends {React.PureComponent<IDobbiesCookieComplianceProps<IHeroConfig>>}

*/

class CookieCompliance extends React.PureComponent<IDobbiesCookieComplianceProps<IDobbiesCookieComplianceData>, IAlertState> {
    private readonly cookieRef: React.RefObject<HTMLInputElement>;

    private cookieCheckInterval: NodeJS.Timeout | null = null;

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,

        this.props.friendlyName,

        this.props.telemetry
    );

    private readonly analyticalCookies: string[] = [
        '_ga',

        '_gid',

        '_gat',

        '_clck',

        '_clsk',

        'ai_session',

        'ai_user',

        'unbxd.userId',

        'unbxd.visit',

        'unbxd.visitId'
    ];

    private readonly functionalCookies: string[] = ['msdyn365___tuid', 'recently-viewed', 'pdp_product_quantity_for_bundles', 'd365mkt'];

    private readonly marketingCookies: string[] = [
        '__qca',

        '_fbp',

        '_gcl_au',

        'IDE',

        'MUID',

        'receive-cookie-deprecation',

        'sa-user-id',

        'sa-user-id-v2',

        'sa-user-id-v3',

        'sa-user-id-v4',

        'dmSessionID',

        'recordID',

        'ddProdInfo'
    ];

    public constructor(props: IDobbiesCookieComplianceProps<IDobbiesCookieComplianceData>) {
        super(props);

        this.state = {
            visible: false,

            checkboxesChecked: [
                {
                    name: CheckboxType.Functional,

                    value: false
                },

                {
                    name: CheckboxType.Analytics,

                    value: false
                },

                {
                    name: CheckboxType.Marketing,

                    value: false
                }
            ]
        };

        this.cookieRef = React.createRef();

        this._onDismiss = this._onDismiss.bind(this);
    }

    /**

     * Updates the shared THG Chatbot analytics consent state.

     *

     * undefined = consent has not been decided yet

     * true      = analytics consent granted

     * false     = analytics consent denied

     */

    private updateTHGConsent = (analytics: boolean | undefined): void => {
        if (!MsDyn365.msdyn365Commerce.isBrowser || typeof window === 'undefined') {
            return;
        }

        window.DobbiesTHGConsent = {
            analytics
        };
    };

    public componentDidMount(): void {
        const { context } = this.props;

        // No consent decision has been made yet until the persisted

        // cookieConsent value is evaluated below.

        this.updateTHGConsent(undefined);

        const cookieConsent = context.request.cookies.get('cookieConsent');

        const recentlyViewed = context.request.cookies.get('recently-viewed');

        if (MsDyn365.msdyn365Commerce.isBrowser && typeof window !== 'undefined') {
            //@ts-ignore

            if (typeof gtag !== 'undefined') {
                //@ts-ignore

                gtag('consent', 'default', {
                    analytics_storage: 'denied',

                    ad_storage: 'denied',

                    ad_user_data: 'denied',

                    ad_personalization: 'denied',

                    functionality_storage: 'denied',

                    security_storage: 'granted',

                    wait_for_update: 2000
                });
            }

            //@ts-ignore

            if (typeof window.clarity !== 'undefined') {
                //@ts-ignore

                window.clarity('consentv2', {
                    ad_storage: 'denied',

                    analytics_storage: 'denied'
                });
            }
        }

        if (context.request.cookies.isConsentGiven() && !cookieConsent?.value && !recentlyViewed?.value) {
            context.request.cookies.deleteConsentCookie();

            this.setState({ visible: true });
        }

        if (context && context.request && context.request.cookies && !context.request.cookies.isConsentGiven()) {
            this.setState({ visible: true });
        }

        if (context?.request?.cookies?.isConsentGiven() && cookieConsent) {
            if (Array.isArray(cookieConsent.value)) {
                let functional = false;

                let analytics = false;

                let marketing = false;

                for (const cc of cookieConsent.value) {
                    if (cc.name === 'Functional' && (cc.value === true || cc.value === 'true')) {
                        functional = true;
                    }

                    if (cc.name === 'Analytics' && (cc.value === true || cc.value === 'true')) {
                        analytics = true;
                    }

                    if (cc.name === 'Marketing' && (cc.value === true || cc.value === 'true')) {
                        marketing = true;
                    }
                }

                // RESTORE CONSENT TO GA4 / GTM / CLARITY

                this.consentGranted(functional, analytics, marketing);

                this.setClarityConsent(marketing, analytics);

                // Restore THG Chatbot analytics consent from the persisted CMP decision.

                this.updateTHGConsent(analytics);

                for (const cc of cookieConsent.value) {
                    if (cc.name === 'Functional' && cc.value === false) {
                        this.rejectFunctionalCookies();
                    }

                    if (cc.name === 'Analytics' && cc.value === false) {
                        this.rejectAnalyticsCookies();
                    }

                    if (cc.name === 'Marketing' && cc.value === false) {
                        this.rejectMarketingCookies();
                    }
                }
            }
        }

        reaction(
            () => this.props.data.showCookieManageModal.result,

            () => {
                if (this.props.data.showCookieManageModal.result?.showCookieManageModal) {
                    this.openManageCookiesModal();
                }
            }
        );

        // Set up interval to check and remove rejected cookies every 3 seconds

        this.cookieCheckInterval = setInterval(() => {
            this.checkAndRemoveRejectedCookies();
        }, 3000);
    }

    public componentWillUnmount(): void {
        // Clear the interval when component unmounts to prevent memory leaks

        if (this.cookieCheckInterval) {
            clearInterval(this.cookieCheckInterval);

            this.cookieCheckInterval = null;
        }
    }

    private checkAndRemoveRejectedCookies = (): void => {
        const { context } = this.props;

        const cookieConsent = context.request.cookies.get('cookieConsent');

        if (!context?.request?.cookies?.isConsentGiven() || !cookieConsent?.value) {
            return;
        }

        if (Array.isArray(cookieConsent.value)) {
            let functionalRejected = true;

            let analyticsRejected = true;

            let marketingRejected = true;

            for (const cc of cookieConsent.value) {
                if (cc.name === 'Functional' && (cc.value === true || cc.value === 'true')) {
                    functionalRejected = false;
                }

                if (cc.name === 'Analytics' && (cc.value === true || cc.value === 'true')) {
                    analyticsRejected = false;
                }

                if (cc.name === 'Marketing' && (cc.value === true || cc.value === 'true')) {
                    marketingRejected = false;
                }
            }

            // If all cookies are rejected, remove all non-necessary cookies

            if (functionalRejected && analyticsRejected && marketingRejected) {
                this.rejectAll(false);

                return;
            }

            // Remove individual rejected cookie categories

            if (functionalRejected) {
                this.rejectFunctionalCookies();
            }

            if (analyticsRejected) {
                this.rejectAnalyticsCookies();
            }

            if (marketingRejected) {
                this.rejectMarketingCookies();
            }
        }
    };

    public handleCheckboxClicked(index: number) {
        const newCheckboxesChecked = [...this.state.checkboxesChecked];

        newCheckboxesChecked[index].value = !newCheckboxesChecked[index].value;

        this.setState({ checkboxesChecked: newCheckboxesChecked });
    }

    public render(): JSX.Element | null {
        const { config, context, resources, telemetry } = this.props;

        const { acceptCookiesButtonText, acceptCookiesAriaLabel } = resources;

        const { visible } = this.state;

        const isConsentGiven = context && context.request && context.request.cookies && context.request.cookies.isConsentGiven();

        let moduleConfig: IDobbiesCookieComplianceConfig;

        try {
            moduleConfig = config;
        } catch (error) {
            telemetry.error(`Something went wrong while rendering the alert module ------${error}`);

            // @ts-ignore

            return <div>{error}</div>;
        }

        // if (isConsentGiven && !visible) {

        //     this.props.context.telemetry.error('Cookie Compliance content is empty, module wont render.');

        //     return null;

        // }

        if (!MsDyn365.msdyn365Commerce.isBrowser) {
            return <></>;
        }

        const viewProps = {
            ...this.props,

            onDismiss: this._onDismiss,

            CookieComplianceBanner: {
                moduleProps: this.props,

                className: classnames('ms-cookie-compliance cookie-redesign', moduleConfig.className),

                id: 'cookieBanner',

                ref: this.cookieRef
            },

            AlertProps: {
                className: 'ms-cookie-compliance__container',

                color: 'white',

                fade: false
            },

            Content: {
                className: 'ms-cookie-compliance__content'
            },

            text: moduleConfig.content && (
                <MsDyn365.RichTextComponent
                    className='ms-cookie-compliance__text'
                    text={moduleConfig.content}
                    editProps={{ onEdit: this.handleContentChange, requestContext: this.props.context.request }}
                />
            ),

            checkboxes: (
                <ul className='ms-cookie-compliance__checkboxes'>
                    <li className='ms-cookie-compliance__checkbox checked'>
                        <input className='ms-cookie-compliance__checkbox__label' type='checkbox' checked={true} />

                        <div>
                            <label>Necessary (required):</label>
                            <p>
                                These are cookies that are required for the operation of our website. They include, for example, cookies
                                that enable you to use a shopping cart
                            </p>
                        </div>
                    </li>
                    <li className='ms-cookie-compliance__checkbox'>
                        <input
                            className='ms-cookie-compliance__checkbox__label'
                            name={this.state.checkboxesChecked[1].name}
                            id={this.state.checkboxesChecked[1].name}
                            type='checkbox'
                            checked={this.state.checkboxesChecked[1].value}
                            onChange={() => this.handleCheckboxClicked(1)}
                        />{' '}
                        <div>
                            <label htmlFor={this.state.checkboxesChecked[1].name}>Analytical:</label>
                            <p>
                                These cookies allow us to recognise and count the number of visitors and to see how visitors move around our
                                website when they are using it. This helps us to improve the way our website works, for example, by ensuring
                                that users are finding what they are looking for easily
                            </p>
                        </div>
                    </li>
                    <li className='ms-cookie-compliance__checkbox'>
                        <input
                            className='ms-cookie-compliance__checkbox__label'
                            name={this.state.checkboxesChecked[0].name}
                            id={this.state.checkboxesChecked[0].name}
                            type='checkbox'
                            checked={this.state.checkboxesChecked[0].value}
                            onChange={() => this.handleCheckboxClicked(0)}
                        />{' '}
                        <div>
                            <label htmlFor={this.state.checkboxesChecked[0].name}>Functional:</label>
                            <p>
                                These cookies are used to recognise you when you return to our website. This enables us to personalise our
                                content for you, greet you by name and remember your preferences (for example, your choice of language or
                                region)
                            </p>
                        </div>
                    </li>
                    <li className='ms-cookie-compliance__checkbox'>
                        <input
                            className='ms-cookie-compliance__checkbox__label'
                            name={this.state.checkboxesChecked[2].name}
                            id={this.state.checkboxesChecked[2].name}
                            type='checkbox'
                            checked={this.state.checkboxesChecked[2].value}
                            onChange={() => this.handleCheckboxClicked(2)}
                        />{' '}
                        <div>
                            <label htmlFor={this.state.checkboxesChecked[2].name}>Marketing:</label>
                            <p>
                                These cookies help us show you advertising that’s relevant to you and assess how effective our marketing is.
                                They may be set by us or by trusted third‑party partners and can track your activity across different
                                websites to build a profile of your interests. We only place these cookies with your explicit consent, which
                                you can withdraw at any time.
                            </p>
                        </div>
                    </li>
                </ul>
            ),

            links: ArrayExtensions.hasElements(moduleConfig.actionLinks) && (
                <CookieComplianceLinks
                    links={moduleConfig.actionLinks}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                    telemetryContent={this.telemetryContent}
                />
            ),

            acceptButton: (
                <CookieComplianceAcceptButton
                    onClose={() => {
                        this._onAcceptAll();
                    }}
                    acceptCookiesButtonText={acceptCookiesButtonText}
                    acceptCookiesButtonAriaLabel={acceptCookiesAriaLabel}
                    telemetryContent={this.telemetryContent}
                />
            ),

            onRejectAll: this.rejectAll,

            onSaveSettings: this.onSaveSettings,

            onManageCookiesClicked: this.openManageCookiesModal,

            onManageCookiesModalClose: this.closeManageCookiesModal,

            showModal: this.state.showModal,

            isModuleVisible: !isConsentGiven && visible
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**

     * Handles Link content change event.

     * @param linkIndex - Index of link in linkarray.

     * @returns Void.

     */

    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        if (this.props.config.actionLinks?.[linkIndex]) {
            this.props.config.actionLinks[linkIndex].linkText = event.target.value;
        }
    };

    /**

     * Handles content change event.

     * @param event - Event.

     */

    public handleContentChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.content = event.target.value;
    };

    private openManageCookiesModal = () => {
        this.setConsentGivenCookiesCheckboxes();

        this.setState({ showModal: true });
    };

    private closeManageCookiesModal = () => {
        this.setState({ showModal: false });
    };

    private setConsentGivenCookiesCheckboxes = () => {
        const cookieConsentCookie = this.props.context.request.cookies.get('cookieConsent')?.value;

        if (cookieConsentCookie && Array.isArray(cookieConsentCookie) && cookieConsentCookie.length) {
            const newCheckboxesChecked = [...this.state.checkboxesChecked];

            for (let i = 0; i < cookieConsentCookie.length; i++) {
                if (cookieConsentCookie[i]['name'] === 'Analytics' && cookieConsentCookie[i]['value'] === true) {
                    newCheckboxesChecked[1].value = true;
                }

                if (cookieConsentCookie[i]['name'] === 'Functional' && cookieConsentCookie[i]['value'] === true) {
                    newCheckboxesChecked[0].value = true;
                }

                if (cookieConsentCookie[i]['name'] === 'Marketing' && cookieConsentCookie[i]['value'] === true) {
                    newCheckboxesChecked[2].value = true;
                }
            }

            this.setState({ checkboxesChecked: newCheckboxesChecked });
        }
    };

    private getNecessaryCookiesRemovedList(cookieNames: string[]): string[] {
        return cookieNames.filter(
            cn =>
                !cn.includes('product') &&
                !cn.includes('notification') &&
                !cn.includes('store') &&
                !cn.includes('popup') &&
                !cn.includes('_msdyn365___cart_') &&
                cn !== '_msdyn365___cart_'
        );
    }

    private rejectAll = (reload: boolean = true) => {
        try {
            const cookies = this.getDobbiesAllCookiesNames();

            const cookieNames: string[] = [];

            for (const [key, value] of Object.entries(cookies)) {
                cookieNames.push(key);

                console.log('Cookie Got', key, value);
            }

            this.removeCookies(this.getNecessaryCookiesRemovedList(cookieNames), this.props.context);
        } catch (error) {
            console.log('Error', error);
        }

        const { context } = this.props;

        if (context && context.request && context.request.cookies) {
            context.request.cookies.setConsentCookie();

            const checkBoxesSelected = this.state.checkboxesChecked;

            for (const checkboxSelection of checkBoxesSelected) {
                checkboxSelection.value = false;
            }

            const cookieConsent = checkBoxesSelected;

            context.request.cookies.set('cookieConsent', cookieConsent, {
                expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365)
            });

            context.request.cookies.set('recently-viewed', [], { expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365) });

            // location.reload();
        }

        this.setState({ visible: false });

        this.setClarityConsent(false, false);

        // Analytics consent is denied, including THG Chatbot analytics.

        this.updateTHGConsent(false);

        this._onDismiss(reload);
    };

    private rejectAnalyticsCookies = () => {
        try {
            const cookies = this.getDobbiesAllCookiesNames();

            const cookieNames: string[] = [];

            for (const [key, value] of Object.entries(cookies)) {
                cookieNames.push(key);

                console.log('Cookie Got', key, value);
            }

            this.removeCookies(
                cookieNames.filter(cn => this.analyticalCookies.includes(cn)),

                this.props.context
            );
        } catch (error) {
            console.log('Error', error);
        }
    };

    private rejectFunctionalCookies = () => {
        try {
            const cookies = this.getDobbiesAllCookiesNames();

            const cookieNames: string[] = [];

            for (const [key, value] of Object.entries(cookies)) {
                cookieNames.push(key);

                console.log('Cookie Got', key, value);
            }

            this.removeCookies(
                this.getNecessaryCookiesRemovedList(cookieNames).filter(cn => this.functionalCookies.includes(cn)),

                this.props.context
            );
        } catch (error) {
            console.log('Error', error);
        }
    };

    private rejectMarketingCookies = () => {
        try {
            const cookies = this.getDobbiesAllCookiesNames();

            const cookieNames: string[] = [];

            for (const [key, value] of Object.entries(cookies)) {
                cookieNames.push(key);

                console.log('Cookie Got', key, value);
            }

            this.removeCookies(
                this.getNecessaryCookiesRemovedList(cookieNames).filter(cn => this.marketingCookies.includes(cn)),

                this.props.context
            );
        } catch (error) {
            console.log('Error', error);
        }
    };

    private getDobbiesAllCookiesNames = () => {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            const cookiePairs = document.cookie.split(';');

            const cookies: { [key: string]: string } = {};

            for (let i = 0; i < cookiePairs.length; i++) {
                const pair = cookiePairs[i].split('=');

                cookies[`${pair[0]}`.trim()] = decodeURIComponent(pair.slice(1).join('='));
            }

            if (this.props.config.additionalCookiesToBeRemoved && this.props.config.additionalCookiesToBeRemoved.length) {
                for (const addCookie of this.props.config.additionalCookiesToBeRemoved) {
                    if (!cookies[addCookie]) {
                        cookies[addCookie] = 'addtional_cookie';
                    }
                }
            }

            return cookies;
        }

        return {};
    };

    private sendGA4Event = (cookieConsentEventData: ICookieConsentEventData) => {
        if (MsDyn365.msdyn365Commerce.isBrowser && typeof window !== 'undefined' && window.dataLayer) {
            //@ts-ignore

            window.dataLayer.push({
                event: 'ga_cookie_consent',

                ga_cookie_consent: [
                    { name: 'Functional', value: cookieConsentEventData.acceptedFunctionalCookies },

                    { name: 'Analytics', value: cookieConsentEventData.acceptedAnalyticalCookies },

                    { name: 'Marketing', value: cookieConsentEventData.acceptedMarketingCookies }
                ]
            });
        }
    };

    private consentGranted = (functional: boolean, analytical: boolean, marketing: boolean) => {
        if (MsDyn365.msdyn365Commerce.isBrowser && typeof window !== 'undefined' && typeof gtag !== 'undefined') {
            //@ts-ignore

            gtag('consent', 'update', {
                analytics_storage: analytical ? 'granted' : 'denied',

                ad_storage: marketing ? 'granted' : 'denied',

                ad_user_data: marketing ? 'granted' : 'denied',

                ad_personalization: marketing ? 'granted' : 'denied',

                functionality_storage: functional ? 'granted' : 'denied',

                security_storage: 'granted'
            });
        }
    };

    private setClarityConsent = (marketing: boolean, analytics: boolean) => {
        if (MsDyn365.msdyn365Commerce.isBrowser && typeof window !== 'undefined' && typeof window.clarity !== 'undefined') {
            //@ts-ignore

            window.clarity('consentv2', {
                ad_storage: marketing ? 'granted' : 'denied',

                analytics_storage: analytics ? 'granted' : 'denied'
            });
        }
    };

    private onSaveSettings = () => {
        if (!this.state.checkboxesChecked[0].value && !this.state.checkboxesChecked[1].value && !this.state.checkboxesChecked[2].value) {
            this.rejectAll();

            const cookieConsentData: ICookieConsentEventData = {
                acceptedAnalyticalCookies: false,

                acceptedFunctionalCookies: false,

                acceptedMarketingCookies: false
            };

            this.sendGA4Event(cookieConsentData);

            this.consentGranted(false, false, false);

            this.setClarityConsent(false, false);

            // Analytics consent is denied, including THG Chatbot analytics.

            this.updateTHGConsent(false);

            return;
        }

        if (!this.state.checkboxesChecked[0].value) {
            this.rejectFunctionalCookies();
        }

        if (!this.state.checkboxesChecked[1].value) {
            this.rejectAnalyticsCookies();
        }

        if (!this.state.checkboxesChecked[2].value) {
            this.rejectMarketingCookies();
        }

        const cookieConsentData: ICookieConsentEventData = {
            acceptedAnalyticalCookies: this.state.checkboxesChecked[1].value,

            acceptedFunctionalCookies: this.state.checkboxesChecked[0].value,

            acceptedMarketingCookies: this.state.checkboxesChecked[2].value
        };

        this.sendGA4Event(cookieConsentData);

        this.consentGranted(
            this.state.checkboxesChecked[0].value,

            this.state.checkboxesChecked[1].value,

            this.state.checkboxesChecked[2].value
        );

        this.setClarityConsent(this.state.checkboxesChecked[2].value, this.state.checkboxesChecked[1].value);

        // Keep THG consent synchronized with the CMP Analytics setting.

        this.updateTHGConsent(this.state.checkboxesChecked[1].value);

        this._onDismiss();
    };

    private removeCookiesForAllDomains(name: string) {
        const cookieStr = `${name.replace(':', '')}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.dynamics.com;`;

        document.cookie = cookieStr;

        const cookieStr2 = `${name.replace(
            ':',

            ''
        )}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=dobbies-acceptance.commerce.dynamics.com;`;

        document.cookie = cookieStr2;

        const cookieStr3 = `${name.replace(':', '')}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=dobbies.com;`;

        document.cookie = cookieStr3;

        const cookieStr4 = `${name.replace(':', '')}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;

        document.cookie = cookieStr4;
    }

    private removeCookies(
        data: string[],

        context: MsDyn365.ICoreContext<{
            [x: string]: any;
        }>
    ) {
        data.forEach(cookieName => {
            if (context.request.cookies.get(cookieName.trim())) {
                const expires = new Date();

                expires.setTime(expires.getTime() - 1);

                document.cookie = `${cookieName.trim()}=; expires=${expires.toUTCString()}; path=/`;

                context.request.cookies.remove(cookieName.trim());

                this.removeCookiesForAllDomains(cookieName.trim());
            }
        });
    }

    private _onDismiss(reload: boolean = true): void {
        const { context } = this.props;

        if (context && context.request && context.request.cookies) {
            context.request.cookies.setConsentCookie();

            const cookieConsent = this.state.checkboxesChecked;

            context.request.cookies.set('cookieConsent', cookieConsent, {
                expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365)
            });

            context.request.cookies.set('recently-viewed', [], { expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365) });

            if (reload) {
                location.reload();
            }
        }

        this.setState({ visible: false });
    }

    private _onAcceptAll(): void {
        const { context } = this.props;

        if (context && context.request && context.request.cookies) {
            context.request.cookies.setConsentCookie();

            const checkBoxesSelected = this.state.checkboxesChecked;

            for (const checkboxSelection of checkBoxesSelected) {
                checkboxSelection.value = true;
            }

            const cookieConsent = checkBoxesSelected;

            context.request.cookies.set('cookieConsent', cookieConsent, {
                expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365)
            });

            context.request.cookies.set('recently-viewed', [], { expires: new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 365) });

            const cookieConsentData: ICookieConsentEventData = {
                acceptedAnalyticalCookies: true,

                acceptedFunctionalCookies: true,

                acceptedMarketingCookies: true
            };

            this.sendGA4Event(cookieConsentData);

            this.consentGranted(true, true, true);

            this.setClarityConsent(true, true);

            // Analytics consent is granted, including THG Chatbot analytics.

            this.updateTHGConsent(true);

            location.reload();
        }

        this.setState({ visible: false });
    }
}

export default CookieCompliance;
